import {create} from 'zustand';
import {isIOSApp, isAndroidApp, isAppWebView} from '../utils/platform';
import {useGlobalAlertModalStore} from './alert';
import {requests} from '../apis/wrapperAPI';
import {User, MessageType} from './types';
import {StatusCodes} from 'http-status-codes';
import {PlatformType} from 'constants/platformType';

export interface SelectedImage {
    id: string;
    file: File | any;
    preview: string;
    originalPath?: string; // 기존 이미지의 상대 경로 (리뷰수정 모드에서 사용)
}

interface InputCallbacks {
    triggerGallery: () => void;
    triggerCamera: () => void;
    clearInput: () => void;
}

interface ImagePickerState {
    selectedImages: SelectedImage[];
    maxCount: number;
    isUploadingImage: boolean;
    isProfileUploadMode: boolean;
    profileUploadCallback: ((user: User) => void) | null;
    isInitialized: boolean;

    // 콜백 함수 (ref 대체)
    triggerGallery: (() => void) | null;
    triggerCamera: (() => void) | null;
    clearInput: (() => void) | null;

    // 액션
    registerInputCallbacks: (callbacks: InputCallbacks) => void;
    handleImagePickerSelect: () => void;
    handleFileChange: (e: React.ChangeEvent<HTMLInputElement>, navigate?: (path: string) => void) => void;
    removeImage: (id: string) => void;
    setSelectedImages: (images: SelectedImage[]) => void;
    initializeImagePicker: () => void;
    handleCameraSelect: () => void;
    clearSelectedImages: () => void;
    setIsUploadingImage: (isUploading: boolean) => void;
    handleProfileImagePickerSelect: (callback: (user: User) => void) => void;
    handleProfileCameraSelect: (callback: (user: User) => void) => void;
    handleProfileFileChange: (e: React.ChangeEvent<HTMLInputElement>, callback: (user: User) => void) => void;
    resetProfileUploadMode: () => void;
}

export const useImagePickerStore = create<ImagePickerState>((set, get) => ({
    selectedImages: [],
    maxCount: 9,
    isUploadingImage: false,
    isProfileUploadMode: false,
    profileUploadCallback: null,
    isInitialized: false,

    // 콜백 함수 초기값
    triggerGallery: null,
    triggerCamera: null,
    clearInput: null,

    setIsUploadingImage: isUploading => set({isUploadingImage: isUploading}),

    registerInputCallbacks: callbacks =>
        set({
            triggerGallery: callbacks.triggerGallery,
            triggerCamera: callbacks.triggerCamera,
            clearInput: callbacks.clearInput,
            isInitialized: true,
        }),

    handleImagePickerSelect: () => {
        get().setIsUploadingImage(true);

        if (isAppWebView()) {
            if (window.ReactNativeWebView) {
                const messageType = MessageType.OPEN_GALLERY;
                const message = JSON.stringify({
                    type: messageType,
                    platform: isIOSApp() ? PlatformType.IOS : PlatformType.ANDROID,
                    maxCount: get().maxCount,
                    enableCrop: false,
                });
                console.log('[IPC Send] OPEN_GALLERY:', message); // 전송 로그
                window.ReactNativeWebView.postMessage(message);
            } else {
                get().setIsUploadingImage(false);
            }
        } else {
            const attemptImageSelect = (retryCount = 0) => {
                const {triggerGallery, isInitialized} = get();
                if (triggerGallery) {
                    triggerGallery();
                } else if (retryCount < 100 && !isInitialized) {
                    // 초기화가 안될시 100번까지 재시도, 100ms 간격
                    setTimeout(() => attemptImageSelect(retryCount + 1), 100);
                } else {
                    console.error('triggerGallery 콜백이 등록되지 않았습니다.');
                    get().setIsUploadingImage(false);
                }
            };
            attemptImageSelect();
        }
    },

    handleCameraSelect: () => {
        if (isAppWebView()) {
            if (window.ReactNativeWebView) {
                const message = JSON.stringify({
                    type: MessageType.OPEN_CAMERA,
                    platform: isIOSApp() ? PlatformType.IOS : PlatformType.ANDROID,
                    maxCount: get().maxCount,
                    enableCrop: false,
                });
                console.log('[IPC Send] OPEN_CAMERA:', message); // 전송 로그
                window.ReactNativeWebView.postMessage(message);
            }
        } else {
            const {triggerCamera} = get();
            if (triggerCamera) {
                triggerCamera();
            }
        }
    },

    handleProfileImagePickerSelect: (callback: (user: User) => void) => {
        set({isProfileUploadMode: true, profileUploadCallback: callback});
        get().setIsUploadingImage(true);

        if (isIOSApp() || isAndroidApp()) {
            if (window.ReactNativeWebView) {
                const messageType = MessageType.OPEN_GALLERY;
                window.ReactNativeWebView.postMessage(
                    JSON.stringify({
                        type: messageType,
                        platform: isIOSApp() ? PlatformType.IOS : PlatformType.ANDROID,
                        maxCount: 1,
                        enableCrop: true,
                    }),
                );
            } else {
                console.error('앱 환경이 아니어서 프로필 갤러리를 열 수 없습니다.');
            }
        } else {
            // const ref = get().fileInputRef;
            // if (ref?.current) {
            //   ref.current.setAttribute('capture', 'environment');
            //   ref.current.click();
            // }
            // 웹에서 input의 'capture' 속성은 브라우저마다 동작이 불안정하여
            // 이 else 블록은 의도적으로 주석 처리 했으며, 웹 전용 카메라 로직은 해당 기능을 사용하는 컴포넌트에서 직접 구현 필요.
            // 작업 예시: myInfo.tsx 에서 직접 CameraCaptureComponent를 사용하도록 구현
        }
    },

    handleProfileCameraSelect: (callback: (user: User) => void) => {
        set({isProfileUploadMode: true, profileUploadCallback: callback});

        if (isIOSApp() || isAndroidApp()) {
            if (window.ReactNativeWebView) {
                window.ReactNativeWebView.postMessage(
                    JSON.stringify({
                        type: MessageType.OPEN_CAMERA,
                        platform: isIOSApp() ? PlatformType.IOS : PlatformType.ANDROID,
                        maxCount: 1,
                        enableCrop: true,
                    }),
                );
            } else {
                console.error('앱 환경이 아니어서 프로필 카메라를 열 수 없습니다.');
            }
        } else {
            // const ref = get().fileInputRef;
            // if (ref?.current) {
            //   ref.current.setAttribute('capture', 'environment');
            //   ref.current.click();
            // }
            // 웹에서 input의 'capture' 속성은 브라우저마다 동작이 불안정하여
            // 이 else 블록은 의도적으로 주석 처리 했으며, 웹 전용 카메라 로직은 해당 기능을 사용하는 컴포넌트에서 직접 구현 필요.
            // 작업 예시: myInfo.tsx 에서 직접 CameraCaptureComponent를 사용하도록 구현
        }
    },

    handleProfileFileChange: async (e: React.ChangeEvent<HTMLInputElement>, callback: (user: User) => void) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            try {
                get().setIsUploadingImage(true);
                const file = files[0];

                const formData = new FormData();
                formData.append('file', file);

                const {isSuccess, message, code, result} = await requests.post(
                    '/api/users/profile-image',
                    true,
                    formData,
                    true,
                );

                if (isSuccess && (code === StatusCodes.OK || code === StatusCodes.CREATED)) {
                    callback(result as User);
                } else {
                    useGlobalAlertModalStore.getState().open(message || '이미지 업로드에 실패했습니다.');
                }
            } catch (error) {
                console.error('프로필 이미지 업로드 오류:', error);
                useGlobalAlertModalStore.getState().open('이미지 업로드에 실패했습니다. 다시 시도해 주세요.');
            } finally {
                get().setIsUploadingImage(false);
                get().resetProfileUploadMode();
            }
        }
        e.target.value = '';
    },

    resetProfileUploadMode: () => {
        set({isProfileUploadMode: false, profileUploadCallback: null});
    },

    handleFileChange: (e, navigate) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            try {
                const {selectedImages, maxCount} = get();

                const remainingSlots = maxCount - selectedImages.length;
                if (remainingSlots <= 0) {
                    useGlobalAlertModalStore.getState().open(`최대 ${maxCount}개 이미지만 선택할 수 있습니다.`);
                    return;
                }

                const newImages = Array.from(files)
                    .slice(0, remainingSlots)
                    .map(file => ({
                        id: Math.random().toString(36).slice(2, 11),
                        file,
                        preview: URL.createObjectURL(file),
                    }));

                set(state => ({
                    selectedImages: [...state.selectedImages, ...newImages],
                }));
            } finally {
                get().setIsUploadingImage(false);
            }
        }
        e.target.value = '';

        get().clearInput?.();
    },

    removeImage: id => {
        set(state => {
            const imageToRemove = state.selectedImages.find(img => img.id === id);
            if (imageToRemove && typeof imageToRemove.file === 'object' && imageToRemove.preview.startsWith('blob:')) {
                URL.revokeObjectURL(imageToRemove.preview);
            }
            return {
                selectedImages: state.selectedImages.filter(img => img.id !== id),
            };
        });
    },

    setSelectedImages: images => set({selectedImages: images}),

    initializeImagePicker: () => {
        const handleMessage = async (event: MessageEvent) => {
            try {
                // 메시지 데이터가 문자열이 아니거나 비어있으면 무시
                if (typeof event.data !== 'string' || !event.data.trim()) {
                    return;
                }

                // 메시지가 JSON 형식인지 확인
                let data;
                try {
                    data = JSON.parse(event.data);
                } catch (parseError) {
                    return;
                }

                if (!data || typeof data !== 'object' || !data.type) {
                    return;
                }

                // GET_PLATFORM_CALLBACK 메시지는 무시 (platform.ts에서 처리)
                if (data.type === 'GET_PLATFORM_CALLBACK') {
                    return;
                }

                if (data.type === MessageType.OPEN_IMAGE_CALLBACK && data.images && Array.isArray(data.images)) {
                    console.log('🔍 OPEN_IMAGE_CALLBACK 메시지 수신:', true);

                    // 이미지를 받은 후 확인 메시지 전송
                    if (window.ReactNativeWebView) {
                        const confirmMessage = JSON.stringify({
                            type: MessageType.GET_IMAGE_RESULT,
                            message: '이미지를 성공적으로 받았습니다',
                            timestamp: new Date().toISOString(),
                        });
                        window.ReactNativeWebView.postMessage(confirmMessage);
                    }

                    const {isProfileUploadMode, profileUploadCallback} = get();

                    if (isProfileUploadMode && profileUploadCallback) {
                        get().setIsUploadingImage(true);

                        if (data.images.length > 0) {
                            const image = data.images[0];

                            try {
                                const byteCharacters = atob(image.base64);
                                const byteNumbers = new Array(byteCharacters.length);
                                for (let i = 0; i < byteCharacters.length; i++) {
                                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                                }
                                const byteArray = new Uint8Array(byteNumbers);
                                const blob = new Blob([byteArray], {type: image.mimeType});

                                const defaultFileName = `picked_image.${image.mimeType.split('/')[1] || 'jpeg'}`;
                                const imageFile = new File([blob], defaultFileName, {type: image.mimeType});

                                const formData = new FormData();
                                formData.append('file', imageFile);

                                const {isSuccess, message, code, result} = await requests.post(
                                    '/api/users/profile-image',
                                    true,
                                    formData,
                                    true,
                                );

                                if (isSuccess && (code === StatusCodes.OK || code === StatusCodes.CREATED)) {
                                    profileUploadCallback(result as User);
                                } else {
                                    useGlobalAlertModalStore
                                        .getState()
                                        .open(message || '이미지 업로드에 실패했습니다.');
                                }
                            } catch (error) {
                                console.error('프로필 이미지 처리 중 오류:', error);
                            }
                        }

                        get().resetProfileUploadMode();
                    } else {
                        get().setIsUploadingImage(true);
                        const {selectedImages, maxCount} = get();

                        const newImagesPromises = data.images.map(async (image: any) => {
                            try {
                                //Base64 ->Blob
                                const byteCharacters = atob(image.base64);
                                const byteNumbers = new Array(byteCharacters.length);
                                for (let i = 0; i < byteCharacters.length; i++) {
                                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                                }
                                const byteArray = new Uint8Array(byteNumbers);
                                const blob = new Blob([byteArray], {type: image.mimeType});

                                //Blob -> File
                                const defaultFileName = `picked_image.${image.mimeType.split('/')[1] || 'jpeg'}`;
                                const imageFile = new File([blob], defaultFileName, {type: image.mimeType});

                                return {
                                    id: Math.random().toString(36).slice(2, 11),
                                    file: imageFile,
                                    preview: URL.createObjectURL(blob),
                                };
                            } catch (error) {
                                throw error;
                            }
                        });

                        try {
                            const newImages = await Promise.all(newImagesPromises);

                            const remainingSlots = maxCount - selectedImages.length;
                            if (remainingSlots <= 0) {
                                useGlobalAlertModalStore
                                    .getState()
                                    .open(`최대 ${maxCount}개 이미지만 선택할 수 있습니다.`);
                                return;
                            }

                            set(state => ({
                                selectedImages: [...state.selectedImages, ...newImages.slice(0, remainingSlots)],
                            }));
                        } catch (error) {
                            console.error('이미지 처리 중 오류가 발생했습니다. :', error);
                        }
                    }
                }
            } catch (error) {
                console.error('이미지 피커 메시지 파싱 오류:', error);
            } finally {
                get().setIsUploadingImage(false);
            }
        };

        const handleMessageWrapper = (event: Event) => {
            handleMessage(event as MessageEvent);
        };

        if (window.ReactNativeWebView && !window.imagePickerMessageListenerRegistered) {
            window.addEventListener('message', handleMessage);
            document.addEventListener('message', handleMessageWrapper);
            window.imagePickerMessageListenerRegistered = true;
            console.log('이미지 피커 이벤트 리스너 등록 완료');
        } else if (window.ReactNativeWebView && window.imagePickerMessageListenerRegistered) {
            console.log('이미지 피커 이벤트 리스너가 이미 등록되어 있습니다.');
        } else {
            console.log('앱 환경이 아니어서 이미지 피커 이벤트 리스너를 등록할 수 없습니다.');
        }
    },
    clearSelectedImages: () => {
        get().selectedImages.forEach(image => {
            if (typeof image.file === 'object' && image.preview.startsWith('blob:')) {
                URL.revokeObjectURL(image.preview);
            }
        });

        set({selectedImages: []});
        get().clearInput?.();
    },
}));
